import { useState } from "react";
import { Box, Typography, Stack, Chip, Popover, IconButton, useTheme, alpha } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const fmt = (n, digits = 2) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

/** Reference for the score's full scale, with the live value highlighted. */
function ScoreScale({ score }) {
  const theme = useTheme();
  const meanings = score?.meanings || [];
  if (meanings.length === 0) return null;
  return (
    <Box sx={{ p: 1.5, maxWidth: 420 }}>
      <Typography variant="caption" fontWeight={800} sx={{ display: "block", mb: 0.75 }}>
        {score.label} scale{score.caption ? ` · ${score.caption}` : ""}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1, rowGap: 0.25 }}>
        {meanings.map(({ score: s, text }) => {
          const active = s === score.value;
          const tone =
            s > 0 ? theme.palette.success.main : s < 0 ? theme.palette.error.main : theme.palette.text.secondary;
          return (
            <Box key={s} sx={{ display: "contents" }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  textAlign: "right",
                  color: tone,
                  bgcolor: active ? alpha(tone, 0.18) : "transparent",
                  borderRadius: 0.5,
                  px: 0.5,
                }}
              >
                {s > 0 ? "+" : ""}
                {s}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: active ? "text.primary" : "text.secondary", fontWeight: active ? 700 : 400 }}
              >
                {text}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** One indicator value. Tone comes from the price comparison, an RSI band, or a signal word. */
function MetricCard({ metric, price }) {
  const theme = useTheme();
  const value = Number(metric.value);
  const known = Number.isFinite(value);

  let diffPct = null;
  if (metric.compareToPrice && known && value > 0 && Number.isFinite(Number(price))) {
    diffPct = ((Number(price) - value) / value) * 100;
  }

  let tone = theme.palette.text.primary;
  let caption = null;

  if (metric.neutral) {
    tone = theme.palette.text.primary;
    caption = diffPct != null ? "vs price" : null;
  } else if (diffPct != null) {
    tone = diffPct > 0 ? theme.palette.success.main : diffPct < 0 ? theme.palette.error.main : theme.palette.text.secondary;
    caption = diffPct > 0 ? "price above" : diffPct < 0 ? "price below" : "at price";
  } else if (metric.format === "index" && known && metric.bands) {
    tone =
      value >= metric.bands.high
        ? theme.palette.warning.main
        : value <= metric.bands.low
          ? theme.palette.info.main
          : theme.palette.text.primary;
    caption = value >= metric.bands.high ? "overbought" : value <= metric.bands.low ? "oversold" : "neutral";
  } else if (metric.format === "signal") {
    tone = value > 0 ? theme.palette.success.main : value < 0 ? theme.palette.error.main : theme.palette.text.secondary;
  }

  const neutralTone = metric.neutral || tone === theme.palette.text.primary;
  const Icon = diffPct > 0 ? TrendingUpIcon : diffPct < 0 ? TrendingDownIcon : RemoveIcon;
  const display =
    metric.format === "signal" ? metric.signalText || (known ? String(value) : "—") : fmt(metric.value);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        height: "100%",
        border: `1px solid ${alpha(tone, 0.25)}`,
        borderLeft: `3px solid ${tone}`,
        background: neutralTone ? "transparent" : `linear-gradient(135deg, ${alpha(tone, 0.07)} 0%, transparent 70%)`,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={800}
        sx={{ letterSpacing: "0.08em", display: "block" }}
      >
        {metric.label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={800}
        letterSpacing="-0.02em"
        sx={{ mt: 0.25, lineHeight: 1.2, color: metric.format === "signal" ? tone : undefined }}
      >
        {display}
      </Typography>
      {diffPct != null ? (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75, color: metric.neutral ? "text.secondary" : tone }}>
          {!metric.neutral && <Icon sx={{ fontSize: "1rem" }} />}
          <Typography variant="caption" fontWeight={metric.neutral ? 600 : 800}>
            {diffPct > 0 ? "+" : ""}
            {diffPct.toFixed(2)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
          {caption || metric.sublabel || (known ? " " : "Not available")}
        </Typography>
      )}
    </Box>
  );
}

/** Shared so the collapsed header and the open body colour the score the same way. */
export const scoreToneFor = (theme, value) =>
  !Number.isFinite(Number(value))
    ? theme.palette.text.secondary
    : Number(value) > 0
      ? theme.palette.success.main
      : Number(value) < 0
        ? theme.palette.error.main
        : theme.palette.text.secondary;

/**
 * Score summary for a section header: current value, what it was, and whether it was rescored
 * against the live quote. Readable while the section is collapsed, which is the point.
 */
export const AlgoScoreBadge = ({ score, compact = false }) => {
  const theme = useTheme();
  const value = Number(score?.value);
  if (!Number.isFinite(value)) {
    return (
      <Typography variant="caption" color="text.disabled">
        no score
      </Typography>
    );
  }
  const tone = scoreToneFor(theme, value);
  const Icon = value === 0 ? RemoveIcon : value > 0 ? TrendingUpIcon : TrendingDownIcon;
  const previous = Number(score?.previous);

  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
      <Chip
        size="small"
        icon={<Icon sx={{ fontSize: "1rem !important" }} />}
        label={`${score.label ? `${score.label} ` : ""}${value > 0 ? "+" : ""}${value}`}
        sx={{
          fontWeight: 800,
          color: tone,
          bgcolor: alpha(tone, 0.14),
          border: `1px solid ${alpha(tone, 0.3)}`,
          "& .MuiChip-icon": { color: tone },
        }}
      />
      {Number.isFinite(previous) && (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          was {previous > 0 ? "+" : ""}
          {previous}
        </Typography>
      )}
      {score.live && !compact && (
        <Chip size="small" variant="outlined" label="live" sx={{ height: 20, fontSize: "0.65rem" }} />
      )}
    </Stack>
  );
};

/**
 * Renders any algorithm's trend values from the shape the API sends. Nothing here knows SMAPC
 * or RSI by name, so a newly onboarded algorithm appears without a front-end change. The score
 * itself lives in the section header; this is the detail behind it.
 */
export const AlgoTrendPanel = ({ panel, price }) => {
  const theme = useTheme();
  const [infoAnchor, setInfoAnchor] = useState(null);
  const infoOpen = Boolean(infoAnchor);

  if (!panel) return null;

  if (!panel.available) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
        No {panel.algoName} trend for this stock - trend data has not been synced.
      </Typography>
    );
  }

  const metrics = (panel.metrics || []).filter((m) => m && m.label);
  const score = panel.score;
  const scoreValue = Number(score?.value);
  const hasScore = Number.isFinite(scoreValue);
  const meaning = (score?.meanings || []).find((m) => m.score === scoreValue)?.text;
  const tone = scoreToneFor(theme, scoreValue);

  if (metrics.length === 0 && !hasScore) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
        No readable values in {panel.trendTable}.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {metrics.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(auto-fit, minmax(132px, 1fr))",
              sm: `repeat(${Math.min(metrics.length, 3)}, 1fr)`,
              // Laptop and up: up to 5 cards per row (panels with 3 cards look the same).
              lg: `repeat(${Math.min(metrics.length, 5)}, 1fr)`,
            },
            gap: { xs: 1.25, sm: 1.5 },
          }}
        >
          {metrics.map((metric) => (
            <MetricCard key={metric.key || metric.label} metric={metric} price={price} />
          ))}
        </Box>
      )}

      {hasScore && (meaning || (score.meanings || []).length > 0) && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 1.25,
            py: 0.75,
            borderRadius: 2,
            border: `1px solid ${alpha(tone, 0.22)}`,
            bgcolor: alpha(tone, 0.05),
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
            {meaning || score.caption}
          </Typography>
          {(score.meanings || []).length > 0 && (
            <IconButton size="small" onClick={(e) => setInfoAnchor(e.currentTarget)} aria-label="Score scale">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}

      <Popover
        open={infoOpen}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <ScoreScale score={score} />
      </Popover>
    </Stack>
  );
};

export default AlgoTrendPanel;
